import { useCallback, useState } from 'react';
import type { Map as OlMap } from 'ol';
import OlFeature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import { MultiPolygon, Polygon } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { intersects as intersectsExtent } from 'ol/extent';
import { getArea } from 'ol/sphere.js';
import { dissolve as turfDissolve } from '@turf/dissolve';
import { flattenEach } from '@turf/meta';
import { featureCollection } from '@turf/helpers';
import type {
  Feature as GeoJSONFeature,
  Polygon as GeoJSONPolygon,
  MultiPolygon as GeoJSONMultiPolygon,
} from 'geojson';
import { ILayers } from 'contexts/@types/Layers';
import { getVectorLayerAttributes } from 'utils/vectorLayerAttributes';

interface UseDissolveLayerParams {
  map: OlMap | undefined;
  handleAddOverlayLayer: (layer: ILayers) => void;
  showAlert: (message: string) => void;
  getUniqueTemporaryLayerTitle: (baseTitle: string) => string;
}

const geoJsonFormat = new GeoJSON();

const removeFileExtension = (name: string) => name.replace(/\.[^/.]+$/, '');

function geometryToTurfFeature(
  geometry: Geometry
): GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> | null {
  const geometryObject = geoJsonFormat.writeGeometryObject(geometry, {
    dataProjection: 'EPSG:3857',
    featureProjection: 'EPSG:3857',
  }) as GeoJSONPolygon | GeoJSONMultiPolygon | null;

  if (!geometryObject) return null;

  if (geometryObject.type !== 'Polygon' && geometryObject.type !== 'MultiPolygon') {
    return null;
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: geometryObject,
  };
}

function turfFeatureToOlGeometry(
  feature: GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon>
): Geometry | null {
  const geometry = geoJsonFormat.readGeometry(feature.geometry, {
    dataProjection: 'EPSG:3857',
    featureProjection: 'EPSG:3857',
  });

  if (geometry instanceof Polygon || geometry instanceof MultiPolygon) {
    return geometry;
  }

  return null;
}

export function useDissolveLayer({
  map,
  handleAddOverlayLayer,
  showAlert,
  getUniqueTemporaryLayerTitle,
}: UseDissolveLayerParams) {
  const [dissolveLayer, setDissolveLayer] = useState<VectorLayer<any> | null>(null);
  const [dissolveAttribute, setDissolveAttribute] = useState<string>('disable');

  const handleSetDissolveLayer = useCallback((id: string) => {
    if (!map || id === 'disable') {
      setDissolveLayer(null);
      setDissolveAttribute('disable');
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find((l: any) => {
        const props = l?.getProperties?.() ?? {};
        return (props.layertitle === id || props.uuid === id) && l instanceof VectorLayer;
      }) as VectorLayer<any> | undefined;

    if (layer) {
      setDissolveLayer(layer);
      setDissolveAttribute('disable');
    }
  }, [map]);

  const handleSetDissolveAttribute = useCallback((attribute: string) => {
    setDissolveAttribute(attribute || 'disable');
  }, []);

  const handleApplyDissolveLayer = useCallback(() => {
    if (!map || !dissolveLayer) {
      showAlert('Selecione uma camada vetorial para aplicar dissolve.');
      return;
    }

    const source = dissolveLayer.getSource() as VectorSource | null;
    if (!source) {
      showAlert('Não foi possível acessar as feições da camada selecionada.');
      return;
    }

    const features = source.getFeatures();

    if (!features || features.length === 0) {
      showAlert('A camada selecionada não possui feições.');
      return;
    }

    const style = features[0].getStyle();

    const polygonFeatures = features.filter((feature) => {
      const geom = feature.getGeometry();
      return geom instanceof Polygon || geom instanceof MultiPolygon;
    }) as OlFeature<Geometry>[];

    if (polygonFeatures.length === 0) {
      showAlert('A ferramenta dissolve funciona apenas com feições poligonais.');
      return;
    }

    const attributeName = dissolveAttribute !== 'disable' ? dissolveAttribute : undefined;
    const turfFeatures: GeoJSONFeature<GeoJSONPolygon>[] = [];
    const largestByGroup = new Map<string, { properties: Record<string, unknown>; area: number }>();

    const featuresWithMeta = polygonFeatures
      .map((feature) => {
        const geometry = feature.getGeometry();
        if (!geometry) return null;
        const turfFeature = geometryToTurfFeature(geometry);
        if (!turfFeature) return null;
        const properties = { ...feature.getProperties() } as Record<string, unknown>;
        delete properties.geometry;
        return {
          properties,
          extent: geometry.getExtent(),
          area: getArea(geometry as Polygon | MultiPolygon),
          turfFeature,
        };
      })
      .filter((item): item is NonNullable<typeof item> => !!item)
      .sort((a, b) => b.area - a.area);

    featuresWithMeta.forEach(({ properties, area, turfFeature }) => {
      if (attributeName) {
        const groupKey = String(properties[attributeName] ?? '');
        const current = largestByGroup.get(groupKey);
        if (!current || area > current.area) {
          largestByGroup.set(groupKey, { properties, area });
        }
      }

      flattenEach(turfFeature as any, (flatFeature) => {
        turfFeatures.push({ ...flatFeature, properties } as GeoJSONFeature<GeoJSONPolygon>);
      });
    });

    if (turfFeatures.length === 0) {
      showAlert('Não foi possível converter as feições da camada selecionada para aplicar o dissolve.');
      return;
    }

    let dissolvedCollection;

    try {
      dissolvedCollection = turfDissolve(
        featureCollection(turfFeatures as any),
        attributeName ? { propertyName: attributeName } : undefined
      );
    } catch (e) {
      showAlert('Ocorreu um erro ao processar o dissolve da camada selecionada.');
      throw e;
    }

    const dissolvedFeatures = dissolvedCollection.features
      .map((feature, index) => {
        const geometry = turfFeatureToOlGeometry(feature);
        if (!geometry) return null;

        let largestProps: Record<string, unknown> = feature.properties ?? {};

        if (attributeName) {
          const groupKey = String((feature.properties || {})[attributeName] ?? '');
          largestProps = largestByGroup.get(groupKey)?.properties ?? largestProps;
        } else {
          const dissolvedExtent = geometry.getExtent();

          for (const { properties, extent } of featuresWithMeta) {
            if (intersectsExtent(dissolvedExtent, extent)) {
              largestProps = properties;
              break;
            }
          }
        }

        const olDissolvedFeature = new OlFeature<Geometry>({
          ...largestProps,
          geometry,
        });
        olDissolvedFeature.setId(`dissolve_${index}_${Date.now()}`);
        olDissolvedFeature.setStyle(style);
        return olDissolvedFeature;
      })
      .filter((feature): feature is OlFeature<Geometry> => !!feature);

    if (dissolvedFeatures.length === 0) {
      showAlert('Não foi possível gerar o dissolve para a camada selecionada.');
      return;
    }

    const dissolveSource = new VectorSource({ features: dissolvedFeatures });
    const timestamp = Date.now();
    const layerName = String(dissolveLayer.get('layertitle') || 'camada');
    const layerTitle = getUniqueTemporaryLayerTitle(`dissolve_${removeFileExtension(layerName)}`);

    const resultLayer: ILayers = {
      id: timestamp,
      area_tematica_id: 'upload',
      sub_tematica_id: null,
      order: 0,
      metadados_camadas_id: null,
      layer_order_id: 'upload',
      layer_order: { order: 0 } as any,
      area_tematica: 'Upload',
      layertitle: layerTitle,
      link_shapefile: '',
      layername: layerTitle,
      host_url: '',
      metadado_url: '',
      layertype: 'VECTOR',
      sourcetype: 'VECTOR',
      visible: true,
      metadados: {
        camada: '',
        categoria: '',
        dado_primario: true,
        data: '',
        fonte: '',
        id: 0,
        organizacao: '',
        representacao: '',
        resumo: '',
        subtema: '',
        tema: '',
        tipo_geom: '',
        tipo_insercao: '',
      },
      nova_camada: true,
      layer_filter_id: 0,
      layer_query_filter: '',
      atributos: getVectorLayerAttributes({
        atributos: [],
        vector_source: dissolveSource,
      }),
      is_vector: true,
      vector_source: dissolveSource,
    };

    handleAddOverlayLayer(resultLayer);

    const extent = dissolveSource.getExtent();
    if (extent && extent.length === 4) {
      map.getView().fit(extent, {
        padding: [40, 40, 40, 40],
        duration: 500,
      });
    }
  }, [map, dissolveLayer, dissolveAttribute, handleAddOverlayLayer, showAlert, getUniqueTemporaryLayerTitle]);

  return {
    dissolveLayer,
    dissolveAttribute,
    handleSetDissolveLayer,
    handleSetDissolveAttribute,
    handleApplyDissolveLayer,
  };
}
